import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Toast } from "./Toast";

/**
 * 商談記録を保存して戻った画面で出すお知らせ（PRODUCT_DEV-783）。
 *
 * 保存すると、商談詳細はすぐ来た画面へ戻る（`state.savedMeeting` を持ち帰る。PRODUCT_DEV-824）。
 * 戻り先はホーム・商談一覧・顧客詳細などばらばらなので、どの画面でも出るよう外枠（RootChrome）に置く。
 * 下書き（音声ドラフト）だった記録は「本登録しました」、本登録済みを直したときは「保存しました」。
 */
const SAVED_MEETING_MESSAGE = {
  registered: "商談記録を本登録しました",
  saved: "商談記録を保存しました",
} as const;

/** 履歴で同じ画面に戻ったときに出し直さないよう、保存の直後だけ出す。 */
const FRESH_MS = 15_000;

interface SavedMeetingState {
  savedMeeting?: { savedAt?: number; wasDraft?: boolean };
}

export function SavedMeetingToast() {
  const location = useLocation();
  const saved = (location.state as SavedMeetingState | null)?.savedMeeting;
  const [shown, setShown] = useState<{ savedAt: number; message: string } | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  useEffect(() => {
    const savedAt = saved?.savedAt;
    if (typeof savedAt !== "number" || savedAt === lastSavedAt) return;
    if (Date.now() - savedAt > FRESH_MS) return;
    setLastSavedAt(savedAt);
    setShown({ savedAt, message: saved?.wasDraft ? SAVED_MEETING_MESSAGE.registered : SAVED_MEETING_MESSAGE.saved });
  }, [saved?.savedAt, saved?.wasDraft, lastSavedAt]);

  // Toast は onClose が変わるとタイマーを掛け直すので、同じ関数を渡し続ける
  const close = useCallback(() => setShown(null), []);

  if (!shown) return null;
  return (
    <Toast
      key={shown.savedAt}
      message={shown.message}
      variant="dark-bottom"
      icon="check"
      onClose={close}
    />
  );
}
